import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
    Phone,
    Calendar,
    Fire,
    ArrowRight,
    CheckCircle,
    ClockCountdown,
    Sparkle,
    UserFocus,
    UsersThree,
    WhatsappLogo,
    Checks,
} from "@phosphor-icons/react";
import DispositionModal from "@/components/DispositionModal";

/**
 * Today — Inbox-zero landing for recruiters (V2.1.1).
 *
 * Nested under CRM (V2.1.1 iter167 — user request). Also exported
 * as `TodayInbox` so the CRM page can render it inline as its
 * default sub-tab, while the standalone `<Today />` page keeps a
 * `/today` alias route for the ad-network deeplinks that already
 * point there.
 *
 * Three ranked sections, top-to-bottom:
 *   1. Callbacks due   — dial NOW (CRM leads)
 *   2. Not reachable   — chase again (CRM leads, attempt_count < 3)
 *   3. Going cold      — Kanban candidates untouched 72h+ (live pipeline)
 */
export default function Today() {
    return (
        <div
            className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1400px] mx-auto"
            data-testid="today-page"
        >
            <TodayInbox />
        </div>
    );
}

export function TodayInbox({ testid = "today-inbox" }) {
    const [data, setData] = useState(null);
    const [view, setView] = useState("mine"); // mine | team
    const [loading, setLoading] = useState(false);
    const [dispTarget, setDispTarget] = useState(null); // {kind, id, name}

    const load = useCallback((v) => {
        setLoading(true);
        api.get(`/today?view=${v}`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load(view);
    }, [view, load]);

    const openDisposition = (row) => {
        setDispTarget({
            kind: row.kind, // crm_lead | candidate
            id: row.id,
            name: row.name,
            phone: row.phone,
        });
    };

    const onDispositionSaved = () => {
        setDispTarget(null);
        load(view);
    };

    const total = data?.counts?.total ?? 0;

    return (
        <div data-testid={testid}>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-cobalt mb-2 inline-flex items-center gap-1">
                        <Sparkle size={12} weight="fill" /> Your day, in one page
                    </p>
                    <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter text-ink mb-2">
                        Today.
                    </h1>
                    <p className="text-sm text-[#525252]">
                        {total > 0 ? (
                            <>
                                <span className="font-bold text-ink">
                                    {total} conversations
                                </span>{" "}
                                waiting for you. Clear them in order.
                            </>
                        ) : (
                            "You're at inbox zero. Nice work."
                        )}
                    </p>
                </div>
                <div className="inline-flex rounded-none border-2 border-ink overflow-hidden self-start">
                    {["mine", "team"].map((v) => (
                        <button
                            key={v}
                            data-testid={`today-view-${v}`}
                            onClick={() => setView(v)}
                            className={`px-4 py-2 text-xs font-black uppercase tracking-wider transition ${
                                view === v
                                    ? "bg-ink text-paper"
                                    : "bg-white text-ink hover:bg-[#F5F5F5]"
                            }`}
                        >
                            {v === "mine" ? (
                                <span className="inline-flex items-center gap-1">
                                    <UserFocus size={12} weight="bold" /> Mine
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1">
                                    <UsersThree size={12} weight="bold" /> Team
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {loading && !data && (
                <p className="text-sm text-[#A3A3A3]">Loading…</p>
            )}

            {data && total === 0 && (
                <EmptyState />
            )}

            {data && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    <Section
                        testid="today-callbacks"
                        title="Callbacks due"
                        eyebrow="Dial now"
                        icon={Phone}
                        tone="urgent"
                        count={data.counts.callbacks_due}
                        rows={data.callbacks_due}
                        emptyMessage="No callbacks scheduled for today."
                        renderRow={(row) => (
                            <LeadRow
                                row={row}
                                onDisposition={() => openDisposition(row)}
                                showTime
                            />
                        )}
                    />
                    <Section
                        testid="today-not-reachable"
                        title="Not reachable"
                        eyebrow="Chase again"
                        icon={ClockCountdown}
                        tone="warn"
                        count={data.counts.not_reachable_followups}
                        rows={data.not_reachable_followups}
                        emptyMessage="No follow-ups queued."
                        renderRow={(row) => (
                            <LeadRow
                                row={row}
                                onDisposition={() => openDisposition(row)}
                                showAttempts
                            />
                        )}
                    />
                    <Section
                        testid="today-warm-reads"
                        title="Read, not registered"
                        eyebrow="Warm — call first"
                        icon={Checks}
                        tone="hot"
                        count={data.counts.warm_reads || 0}
                        rows={data.warm_reads || []}
                        emptyMessage="No unread-to-signup gaps this week."
                        renderRow={(row) => (
                            <LeadRow
                                row={row}
                                onDisposition={() => openDisposition(row)}
                                showReadAt
                            />
                        )}
                    />
                    <Section
                        testid="today-hot-kanban"
                        title="Going cold"
                        eyebrow="Kanban rescue"
                        icon={Fire}
                        tone="cold"
                        count={data.counts.hot_kanban}
                        rows={data.hot_kanban}
                        emptyMessage="No stalled candidates."
                        renderRow={(row) => (
                            <CandRow
                                row={row}
                                onDisposition={() => openDisposition(row)}
                            />
                        )}
                    />
                </div>
            )}

            {dispTarget && (
                <DispositionModal
                    open
                    onClose={() => setDispTarget(null)}
                    targetType={dispTarget.kind}
                    targetId={dispTarget.id}
                    onSubmitted={onDispositionSaved}
                />
            )}
        </div>
    );
}

// ---------------- Section wrapper ----------------
function Section({ testid, title, eyebrow, icon: Icon, tone, count, rows, emptyMessage, renderRow }) {
    const toneStyles = {
        urgent: "border-[#DC2626] text-[#DC2626]",
        warn: "border-[#B45309] text-[#B45309]",
        cold: "border-cobalt text-cobalt",
        hot: "border-emerald-600 text-emerald-700",
    };
    return (
        <div
            data-testid={testid}
            className="bg-white border border-[#E5E5E5] flex flex-col"
        >
            <div className={`flex items-center justify-between px-4 py-3 border-b-2 ${toneStyles[tone]}`}>
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em]">
                        {eyebrow}
                    </p>
                    <h2 className="font-heading text-lg font-black tracking-tight text-ink inline-flex items-center gap-2 mt-0.5">
                        <Icon size={16} weight="bold" /> {title}
                    </h2>
                </div>
                <span className="font-heading font-black text-3xl tracking-tighter text-ink">
                    {count}
                </span>
            </div>
            <div className="flex-1 divide-y divide-[#E5E5E5] min-h-[80px]">
                {rows.length === 0 ? (
                    <p className="p-6 text-xs text-[#A3A3A3] text-center">
                        {emptyMessage}
                    </p>
                ) : (
                    rows.map((r) => (
                        <div key={r.id}>{renderRow(r)}</div>
                    ))
                )}
            </div>
        </div>
    );
}

// ---------------- Rows ----------------
function LeadRow({ row, onDisposition, showTime = false, showAttempts = false, showReadAt = false }) {
    const wa = row.phone
        ? `https://wa.me/${row.phone.replace(/\D/g, "")}?text=${encodeURIComponent(
              `Hi ${(row.name || "").split(" ")[0] || "there"}, this is from NaukriDaddy — following up on your interest.`
          )}`
        : null;
    const dueTxt = row.next_action_at
        ? new Date(row.next_action_at).toLocaleString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              day: "2-digit",
              month: "short",
          })
        : "Now";
    return (
        <div
            className="px-4 py-3 hover:bg-[#F9F9F9] transition"
            data-testid={`today-lead-${row.id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm text-ink truncate">
                        {row.name || "(no name)"}
                    </p>
                    <p className="text-xs text-[#525252] truncate">
                        {row.current_title || row.role_interest || "—"} · {row.city || "—"}
                    </p>
                    {showTime && (
                        <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[#DC2626]">
                            <ClockCountdown size={10} weight="bold" className="inline mr-0.5" />
                            {dueTxt}
                        </p>
                    )}
                    {showAttempts && (
                        <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[#B45309]">
                            Attempt {row.attempt_count + 1} of 3
                        </p>
                    )}
                    {showReadAt && row.read_at && (
                        <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700" data-testid={`today-lead-read-${row.id}`}>
                            <Checks size={10} weight="bold" className="inline mr-0.5" />
                            Read {new Date(row.read_at).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                        </p>
                    )}
                </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
                {row.phone ? (
                    <a
                        href={`tel:${row.phone}`}
                        data-testid={`today-lead-call-${row.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 border border-ink bg-white text-ink text-[10px] font-black uppercase tracking-wider hover:bg-ink hover:text-paper transition"
                    >
                        <Phone size={10} weight="bold" /> Call
                    </a>
                ) : null}
                {wa ? (
                    <a
                        href={wa}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`today-lead-wa-${row.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[#25D366] text-white text-[10px] font-black uppercase tracking-wider hover:brightness-95 transition"
                    >
                        <WhatsappLogo size={10} weight="fill" /> WA
                    </a>
                ) : null}
                <button
                    onClick={onDisposition}
                    data-testid={`today-lead-disp-${row.id}`}
                    className="ml-auto inline-flex items-center gap-1 px-2 py-1 bg-cobalt text-white text-[10px] font-black uppercase tracking-wider hover:bg-ink transition"
                >
                    Log outcome <ArrowRight size={10} weight="bold" />
                </button>
            </div>
        </div>
    );
}

function CandRow({ row, onDisposition }) {
    const daysStale = row.last_touched_at
        ? Math.floor(
              (Date.now() - new Date(row.last_touched_at).getTime()) / 86_400_000
          )
        : 0;
    return (
        <div
            className="px-4 py-3 hover:bg-[#F9F9F9] transition"
            data-testid={`today-cand-${row.id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm text-ink truncate">
                        {row.name || "(no name)"}
                    </p>
                    <p className="text-xs text-[#525252] truncate">
                        {row.job_title || "—"} · {row.stage}
                    </p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-cobalt">
                        <Fire size={10} weight="fill" className="inline mr-0.5" />
                        Cold for {daysStale}d
                    </p>
                </div>
                {typeof row.match_score === "number" && (
                    <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-[#EFF6FF] text-cobalt shrink-0">
                        {row.match_score}%
                    </span>
                )}
            </div>
            <div className="mt-2 flex items-center gap-2">
                <Link
                    to={`/jobs/${row.job_id}`}
                    data-testid={`today-cand-open-${row.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-ink bg-white text-ink text-[10px] font-black uppercase tracking-wider hover:bg-ink hover:text-paper transition"
                >
                    Open Kanban
                </Link>
                <button
                    onClick={onDisposition}
                    data-testid={`today-cand-disp-${row.id}`}
                    className="ml-auto inline-flex items-center gap-1 px-2 py-1 bg-cobalt text-white text-[10px] font-black uppercase tracking-wider hover:bg-ink transition"
                >
                    Log outcome <ArrowRight size={10} weight="bold" />
                </button>
            </div>
        </div>
    );
}

// ---------------- Empty state ----------------
function EmptyState() {
    return (
        <div
            data-testid="today-empty"
            className="bg-white border border-[#E5E5E5] p-12 text-center"
        >
            <CheckCircle
                size={56}
                weight="fill"
                className="mx-auto text-[#059669] mb-4"
            />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#059669] mb-2">
                Inbox zero
            </p>
            <h2 className="font-heading text-3xl font-black tracking-tighter text-ink mb-3">
                Nothing waiting.
            </h2>
            <p className="text-sm text-[#525252] max-w-md mx-auto">
                No callbacks are due, no leads need chasing, and every active
                candidate has a recent touch. Grab a coffee — or go source new
                candidates from the{" "}
                <Link to="/candidates" className="underline underline-offset-2 text-cobalt font-bold">
                    Candidates
                </Link>{" "}
                pool.
            </p>
        </div>
    );
}
